import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import api from '../../services/api';

const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 24px;
  margin: 0;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #ff6b35, #ff8c42);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  background: ${props => props.$active ? '#ff6b35' : '#2d2d2d'};
  color: ${props => props.$active ? '#fff' : '#999'};
  border: 1px solid ${props => props.$active ? '#ff6b35' : '#444'};
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$active ? '#ff6b35' : '#3d3d3d'};
    color: #fff;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 12px 16px;
  background: #2d2d2d;
  border: 1px solid #444;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const Table = styled.table`
  width: 100%;
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: #2d2d2d;
  color: #ff6b35;
  padding: 15px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #ff6b35;
`;

const Td = styled.td`
  padding: 15px;
  color: #fff;
  border-bottom: 1px solid #333;
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.$status) {
      case 'active': return '#4caf50';
      case 'expired': return '#f44336';
      case 'cancelled': return '#9e9e9e';
      default: return '#2196f3';
    }
  }};
  color: white;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 16px;
  padding: 30px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  color: #ff6b35;
  margin-bottom: 20px;
  font-size: 22px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: #fff;
  margin-bottom: 8px;
  font-weight: 600;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  background: #2d2d2d;
  border: 1px solid #444;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  background: #2d2d2d;
  border: 1px solid #444;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const CancelButton = styled(Button)`
  background: #666;

  &:hover {
    background: #777;
  }
`;

const Message = styled.div`
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'error' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)'};
  border: 1px solid ${props => props.$type === 'error' ? '#f44336' : '#4caf50'};
  color: ${props => props.$type === 'error' ? '#f44336' : '#4caf50'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
`;

const AdminMemberships = () => {
  const [memberships, setMemberships] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const statusFilter = filter === 'all' ? null : filter;
      const [membershipsRes, customersRes, plansRes] = await Promise.all([
        api.adminGetMemberships(statusFilter),
        api.adminGetCustomers(),
        api.getMembershipPlans()
      ]);
      
      setMemberships(membershipsRes.data || []);
      setCustomers(customersRes.data || []);
      setPlans(plansRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const handleActivateMembership = async () => {
    if (!selectedCustomer || !selectedPlan) {
      setMessage({ type: 'error', text: 'Please select both customer and plan' });
      return;
    }

    try {
      await api.adminActivateMembership({
        user_id: selectedCustomer,
        plan_id: selectedPlan,
        payment_method: 'cash'
      });

      setMessage({ type: 'success', text: 'Membership activated successfully!' });
      setShowModal(false);
      setSelectedCustomer('');
      setSelectedPlan('');
      fetchData();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to activate membership' 
      });
    }
  };

  const handleDeactivate = async (membershipId) => {
    if (!window.confirm('Are you sure you want to deactivate this membership?')) {
      return;
    }

    try {
      await api.adminDeactivateMembership(membershipId);
      setMessage({ type: 'success', text: 'Membership deactivated successfully' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to deactivate membership' });
    }
  };

  return (
    <Container>
      <Header>
        <Title>Membership Management</Title>
        <Button onClick={() => setShowModal(true)}>
          + Activate Membership
        </Button>
      </Header>

      {message.text && (
        <Message $type={message.type}>
          {message.text}
        </Message>
      )}

      <FilterContainer>
        <FilterButton $active={filter === 'all'} onClick={() => setFilter('all')}>
          All
        </FilterButton>
        <FilterButton $active={filter === 'active'} onClick={() => setFilter('active')}>
          Active
        </FilterButton>
        <FilterButton $active={filter === 'expired'} onClick={() => setFilter('expired')}>
          Expired
        </FilterButton>
        <FilterButton $active={filter === 'cancelled'} onClick={() => setFilter('cancelled')}>
          Cancelled
        </FilterButton>
      </FilterContainer>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search by phone number..."
          value={phoneFilter}
          onChange={(e) => setPhoneFilter(e.target.value)}
        />
      </SearchContainer>

      {loading ? (
        <EmptyState>Loading...</EmptyState>
      ) : memberships.length === 0 ? (
        <EmptyState>No memberships found</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Customer</Th>
              <Th>Plan</Th>
              <Th>Start Date</Th>
              <Th>End Date</Th>
              <Th>Clothes Used</Th>
              <Th>Remaining</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {memberships.filter(membership => {
              if (!phoneFilter.trim()) return true;
              const phone = membership.user_phone || '';
              return phone.includes(phoneFilter.trim());
            }).map((membership) => (
              <tr key={membership.id}>
                <Td>
                  <div>{membership.user_name}</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{membership.user_email}</div>
                </Td>
                <Td>{membership.plan_name}</Td>
                <Td>{new Date(membership.start_date).toLocaleDateString()}</Td>
                <Td>{new Date(membership.end_date).toLocaleDateString()}</Td>
                <Td>{membership.clothes_used}</Td>
                <Td>{membership.clothes_remaining}</Td>
                <Td>
                  <StatusBadge $status={membership.status}>
                    {membership.status}
                  </StatusBadge>
                </Td>
                <Td>
                  {membership.status === 'active' && (
                    <Button 
                      onClick={() => handleDeactivate(membership.id)}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      Deactivate
                    </Button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {showModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowModal(false)}
        >
          <ModalContent
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalTitle>Activate Membership</ModalTitle>

            <FormGroup>
              <Label>Search Customer (by phone or email)</Label>
              <Input
                type="text"
                placeholder="Enter phone number or email..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Select Customer</Label>
              <Select 
                value={selectedCustomer} 
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                <option value="">Choose a customer...</option>
                {customers
                  .filter(customer => {
                    if (!customerSearch) return true;
                    const search = customerSearch.toLowerCase();
                    return (
                      customer.phone?.toLowerCase().includes(search) ||
                      customer.email?.toLowerCase().includes(search)
                    );
                  })
                  .map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} - {customer.email} - {customer.phone}
                    </option>
                  ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Select Plan</Label>
              <Select 
                value={selectedPlan} 
                onChange={(e) => setSelectedPlan(e.target.value)}
              >
                <option value="">Choose a plan...</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ₹{plan.price} ({plan.clothes_limit + plan.bonus_clothes} clothes)
                  </option>
                ))}
              </Select>
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={() => setShowModal(false)}>
                Cancel
              </CancelButton>
              <Button onClick={handleActivateMembership}>
                Activate
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminMemberships;
